import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Services() {
  const navigate = useNavigate();
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ rootMargin: '0px 0px -50px 0px' });

  const services = [
    {
      icon: 'ri-goblet-line',
      title: 'Glassware Hire',
      description: 'Elegant wine glasses, champagne flutes, cocktail glasses, tumblers & more.',
      link: '/equipment-hire#glassware'
    },
    {
      icon: 'ri-beer-line',
      title: 'Bar Equipment',
      description: 'Portable bars, bar tools, ice tubs, shelving, service stations.',
      link: '/equipment-hire#bars'
    },
    {
      icon: 'ri-gift-line',
      title: 'Event Essentials',
      description: 'Tables, service items, styling pieces, accessories.',
      link: '/equipment-hire'
    },
    {
      icon: 'ri-truck-line',
      title: 'Delivery & Collection',
      description: 'Professional setup and pack-down services available.',
      link: '/contact'
    }
  ];

  const handleServiceClick = (link: string) => {
    if (link.includes('#')) {
      const [path, hash] = link.split('#');
      navigate(path);
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      navigate(link);
    }
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-14 scroll-animate ${headerVisible ? 'visible' : ''}`}
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#D4AF37]"></div>
            <span className="text-[#D4AF37] text-xs font-semibold tracking-[0.25em] uppercase">What We Offer</span>
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#D4AF37]"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">Our Services</h2>
          <p className="text-gray-500 max-w-xl mx-auto text-base leading-relaxed">
            Everything you need for a flawless event, delivered with precision across Melbourne.
          </p>
        </div>

        {/* Services Grid */}
        <div
          ref={gridRef}
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 stagger-children ${gridVisible ? 'visible' : ''}`}
        >
          {services.map((service, index) => (
            <div
              key={index}
              onClick={() => handleServiceClick(service.link)}
              className="group cursor-pointer bg-white border border-gray-200 hover:border-[#D4AF37] transition-all duration-300 p-8 text-center hover:shadow-xl"
            >
              {/* Icon */}
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-gradient-to-br from-[#D4AF37]/10 to-[#D4AF37]/5 rounded-full group-hover:from-[#D4AF37]/20 group-hover:to-[#D4AF37]/10 transition-all duration-300">
                <i className={`${service.icon} text-3xl text-[#D4AF37]`}></i>
              </div>

              {/* Title */}
              <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-[#D4AF37] transition-colors duration-300">
                {service.title}
              </h3>

              {/* Description */}
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                {service.description}
              </p>

              {/* Arrow */}
              <div className="flex items-center justify-center text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="text-sm font-medium mr-2">Learn More</span>
                <i className="ri-arrow-right-line text-lg"></i>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}